/**
 * Caps the scale passed to PDF.js / image rasterization so large construction
 * sheets do not allocate 40M+ pixel canvases when the viewer zoom exceeds ~300%.
 * Visual zoom still uses displayScale; CSS transform handles the remainder.
 */

/** Target max canvas area (width × height in px). Safe on most browsers. */
const MAX_RENDER_PIXELS = 10_000_000;
/** Conservative per-side limit (Chrome often allows 16384; stay well under). */
const MAX_RENDER_DIMENSION = 6144;

/**
 * Returns the scale to use for PDF.js <Page scale={…}> / image intrinsic size.
 * Never exceeds `displayScale`, but may be lower when the page is very large.
 */
export function capPdfRenderScale(
  displayScale: number,
  pageWidthPt: number,
  pageHeightPt: number
): number {
  if (displayScale <= 0 || pageWidthPt <= 0 || pageHeightPt <= 0) return displayScale;

  let cap = displayScale;
  const w = pageWidthPt * displayScale;
  const h = pageHeightPt * displayScale;

  if (w * h > MAX_RENDER_PIXELS) {
    cap = Math.min(cap, Math.sqrt(MAX_RENDER_PIXELS / (pageWidthPt * pageHeightPt)));
  }
  if (w > MAX_RENDER_DIMENSION) {
    cap = Math.min(cap, MAX_RENDER_DIMENSION / pageWidthPt);
  }
  if (h > MAX_RENDER_DIMENSION) {
    cap = Math.min(cap, MAX_RENDER_DIMENSION / pageHeightPt);
  }

  return Math.max(0.05, cap);
}

/** Skip text-layer render when CSS upscale is large (saves time, text is soft anyway). */
export function shouldRenderPdfTextLayer(zoomRatio: number): boolean {
  return zoomRatio <= 1.6;
}
